import {useGrpcRequest} from '@aboutyou/data-fetcher';
import {
  NavigationPageService_GetBrowseSectionsLazy,
  NavigationPageService_GetBrowseSectionsNextLazy,
} from '@aboutyou/grpc-clients/tadarida/aysa_api/services/navigation_page/v1/service.grpc.lazy';
import {useHydrationDone} from '@aboutyou/lazy-imports';
import {useFullServerSession, useFullSession} from '@aboutyou/user';
import {useRef} from 'react';
import {useStaleLastSeenProductIds} from '../../../features/LastSeenProducts/useLastSeenProductIds';
import {useReductionsStateRequestParameter} from '../../../features/ReductionsState/useReductionsStateRequestParameter';
import {useTargetGroupIdentifier} from '../../../shared-deprecated/useTargetGroupIdentifier';

export const useGetBrowseSections = () => {
  const session = useFullServerSession();
  const targetGroup = useTargetGroupIdentifier();
  const reductionsState = useReductionsStateRequestParameter();
  const isHydrationDone = useHydrationDone();
  const lastSeenProductIds = useStaleLastSeenProductIds({
    uniqueKey: 'BROWSE_LAST_SEEN_PRODUCT_IDS',
  });

  const request = useGrpcRequest(
    NavigationPageService_GetBrowseSectionsLazy,
    {
      session,
      targetGroup,
      reductionsState,
      // Use empty array during SSR to prevent hydration mismatches since lastSeenProductIds aren't available server-side
      lastSeenProductIds: isHydrationDone ? lastSeenProductIds : [],
      // according to BE, limit 1 will return always the live shopping, target group and spotlight
      limit: 1,
    },
    {
      keepPreviousData: true,
      disabled: process.browser && !session,
    },
  );

  const nextState =
    request.data?.items?.[request.data?.items.length - 1]?.continuationToken;

  /**
   * When changing gender, the spotlight slider is not present initially.
   * This logic makes sure to return the previous data when spotlight is not present.
   * This should not happen when logging in, otherwise we'll end-up showing corrupted data, like double spotlight slider.
   */
  const previousData = useRef(request.data);
  let finalData = request.data;

  if (
    !request.data?.items?.find(el => el.section.type?.$case === 'spotlight')
  ) {
    finalData = previousData.current;
  }

  return {
    ...request,
    data: finalData
      ? {
          ...finalData,
          nextState,
        }
      : undefined,
  };
};

export const useGetBrowseNextSections = ({token}: {token: string}) => {
  const fullSession = useFullSession();
  const targetGroup = useTargetGroupIdentifier();
  const reductionsState = useReductionsStateRequestParameter();
  const lastSeenProductIds = useStaleLastSeenProductIds({
    uniqueKey: 'BROWSE_LAST_SEEN_PRODUCT_IDS',
  });

  const request = useGrpcRequest(
    NavigationPageService_GetBrowseSectionsNextLazy,
    {
      session: fullSession!,
      targetGroup,
      reductionsState,
      lastSeenProductIds,
      limit: 5,
      continuationToken: token,
      deprecatedContinuationToken: {token: new Uint8Array()},
    },
    {
      keepPreviousData: true,
      disabled: !fullSession,
    },
  );

  const nextState =
    request.data?.items?.[request.data?.items.length - 1]?.continuationToken;

  return {
    ...request,
    data: request.data
      ? {
          ...request.data,
          nextState,
        }
      : undefined,
  };
};
